import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const cleanMessage = text =>
  text
    ?.replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/\s+([.,!?])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim() ?? '';

const splitBoldSegments = text => {
  if (!text) return [];

  return text
    .split(/(<b>.*?<\/b>)/g)
    .filter(Boolean)
    .map(segment => {
      const isBold = segment.startsWith('<b>') && segment.endsWith('</b>');

      return {
        isBold,
        content: isBold
          ? segment.replace(/^<b>/, '').replace(/<\/b>$/, '')
          : segment,
      };
    });
};

const getVisibleTextLength = text =>
  splitBoldSegments(text).reduce(
    (total, segment) => total + segment.content.length,
    0,
  );

const renderTypedMessage = (text, visibleChars = null) => {
  const segments = splitBoldSegments(text);

  if (!segments.length) return null;

  let remainingChars =
    visibleChars === null ? getVisibleTextLength(text) : visibleChars;

  return segments.map((segment, index) => {
    if (remainingChars <= 0) return null;

    const visibleContent = segment.content.slice(0, remainingChars);
    remainingChars -= visibleContent.length;

    if (!segment.isBold) {
      return (
        <Box key={`segment-${index}`} component="span">
          {visibleContent}
        </Box>
      );
    }

    return (
      <Box key={`segment-${index}`} component="strong" sx={{ fontWeight: 700 }}>
        {visibleContent}
      </Box>
    );
  });
};

const resolveBannerIcon = msgs =>
  msgs.banner_icon ||
  msgs.tasks_icon ||
  msgs.welcome_icon ||
  msgs.icon ||
  'sparkles-outline';

const resolveBannerIconColor = msgs =>
  msgs.banner_icon_color || msgs.icon_color || null;

const styles = {
  card: {
    width: '100%',
    height: { xs: 'auto', md: '100%' },
    boxShadow: 2,
    overflow: 'hidden',
    display: 'flex',
    background: theme =>
      `linear-gradient(90deg, ${theme.palette.mode === 'dark' ? '#0F172A' : '#1E3668'
      } 0%, ${theme.palette.mode === 'dark' ? '#1A0309' : '#122240'} 100%)`,
    border: theme =>
      `1px solid ${theme.palette.mode === 'dark'
        ? theme.bruno.colors.borderCard
        : 'transparent'
      }`,
  },
  cardContent: {
    p: 0,
    flex: 1,
    display: 'flex',
    '&:last-child': { pb: 0 },
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  loadingInner: {
    minHeight: { xs: 74, sm: 82, md: 108 },
  },
  robotBox: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    px: { xs: 2, md: 2.5 },
    py: 1,
    minWidth: { xs: 88, md: 96 },
  },
  robotImg: {
    width: { xs: 54, md: 70 },
    objectFit: 'contain',
  },
  textBox: {
    flexGrow: 1,
    px: { xs: 1.5, md: 2.25 },
    py: 1.5,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  name: {
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'common.white',
    fontSize: { xs: '0.95rem', md: '1.15rem', lg: '1.2rem' },
  },
  message: {
    mt: 0.25,
    color: 'rgba(255,255,255,0.85)',
    fontSize: { xs: '0.72rem', md: '0.9rem', lg: '0.95rem' },
    lineHeight: { xs: 1.45, md: 1.5 },
  },
  skeletonTitle: {
    bgcolor: 'rgba(255,255,255,0.22)',
    borderRadius: 1,
  },
  skeletonLine: {
    bgcolor: 'rgba(255,255,255,0.16)',
    borderRadius: 1,
    height: 10,
  },
};

export default function HomeHeader({
  firstName,
  botMessages,
  botMessagesLoading,
  currentStore,
  getBotMessages,
}) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [activeHeaderText, setActiveHeaderText] = useState('');
  const [visibleHeaderChars, setVisibleHeaderChars] = useState(0);
  const timerRef = useRef(null);
  const animationRunIdRef = useRef(0);
  const lastAnimatedSignatureRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleStep = (callback, delay) => {
    clearTimer();
    timerRef.current = setTimeout(callback, delay);
  };

  const typeHeaderText = (text, runId, onComplete) => {
    const totalChars = getVisibleTextLength(text);

    const step = currentChars => {
      if (runId !== animationRunIdRef.current) return;

      setVisibleHeaderChars(currentChars);

      if (currentChars >= totalChars) {
        if (onComplete) onComplete();
        return;
      }

      scheduleStep(() => step(currentChars + 1), 28);
    };

    step(0);
  };

  const eraseHeaderText = (text, runId, onComplete) => {
    const totalChars = getVisibleTextLength(text);

    const step = currentChars => {
      if (runId !== animationRunIdRef.current) return;

      setVisibleHeaderChars(currentChars);

      if (currentChars <= 0) {
        if (onComplete) onComplete();
        return;
      }

      scheduleStep(() => step(currentChars - 1), 18);
    };

    step(totalChars);
  };

  // Only fetch on mount or store change — not on language change.
  // The API returns all languages in one response; we select client-side below.
  useEffect(() => {
    if (currentStore) getBotMessages(i18n.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, getBotMessages]);

  // Select the right language from the multi-language response stored in Redux.
  const msgs =
    botMessages?.[i18n.language] ?? botMessages?.['en'] ?? botMessages ?? {};
  const welcomeMessage =
    cleanMessage(msgs.welcome_message) ||
    cleanMessage(msgs.tasks_resume_header) ||
    `${firstName}!`;
  const resumeHeader = cleanMessage(msgs.tasks_resume_header) || '';
  const resumeMessage =
    cleanMessage(msgs.tasks_resume_body) ||
    cleanMessage(msgs.tasks_resume) ||
    cleanMessage(msgs.tasks_message) ||
    '';
  const bannerIcon = resolveBannerIcon(msgs);
  const bannerIconColor = resolveBannerIconColor(msgs) || '#46bf83';

  useEffect(() => {
    if (botMessagesLoading) {
      clearTimer();
      return undefined;
    }

    const signature = `${welcomeMessage}|||${resumeHeader}`;

    if (!welcomeMessage && !resumeHeader) {
      setActiveHeaderText('');
      setVisibleHeaderChars(0);
      return undefined;
    }

    if (lastAnimatedSignatureRef.current === signature) {
      return undefined;
    }

    lastAnimatedSignatureRef.current = signature;
    animationRunIdRef.current += 1;
    const runId = animationRunIdRef.current;

    if (!welcomeMessage) {
      setActiveHeaderText(resumeHeader);
      setVisibleHeaderChars(getVisibleTextLength(resumeHeader));
      return undefined;
    }

    setActiveHeaderText(welcomeMessage);
    setVisibleHeaderChars(0);

    typeHeaderText(welcomeMessage, runId, () => {
      if (!resumeHeader || resumeHeader === welcomeMessage) return;

      scheduleStep(() => {
        if (runId !== animationRunIdRef.current) return;

        eraseHeaderText(welcomeMessage, runId, () => {
          setActiveHeaderText(resumeHeader);
          setVisibleHeaderChars(0);
          typeHeaderText(resumeHeader, runId);
        });
      }, 1100);
    });

    return () => clearTimer();
  }, [botMessagesLoading, welcomeMessage, resumeHeader]);

  const headerTextForRender = activeHeaderText || welcomeMessage;
  const headerVisibleChars = activeHeaderText ? visibleHeaderChars : null;

  return (
    <Card sx={styles.card}>
      <CardContent sx={styles.cardContent}>
        <Box
          sx={{
            ...styles.inner,
            ...(botMessagesLoading ? styles.loadingInner : {}),
          }}
        >
          {/* Robot image */}
          <Box sx={styles.robotBox}>
            <Box
              component="img"
              src="/bruno_mascot.png"
              alt="Bruno"
              sx={styles.robotImg}
            />
            <Box
              sx={theme => ({
                position: 'absolute',
                top: { xs: 6, md: 8 },
                right: { xs: 10, md: 12 },
                width: { xs: 24, md: 28 },
                height: { xs: 24, md: 28 },
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? bannerIconColor
                    : theme.palette.common.white,
                border: `1px solid ${theme.palette.mode === 'dark'
                  ? bannerIconColor
                  : 'rgba(255,255,255,0.55)'
                  }`,
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? 'none'
                    : '0 2px 8px rgba(0,0,0,0.10)',
              })}
            >
              {botMessagesLoading ? (
                <Skeleton
                  variant="circular"
                  width={14}
                  height={14}
                  sx={theme => ({
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.22)'
                        : 'rgba(0,0,0,0.10)',
                  })}
                />
              ) : (
                <IconComponent
                  iconName={bannerIcon}
                  style={{
                    fontSize: 16,
                    color:
                      theme.palette.mode === 'dark' ? '#fff' : bannerIconColor,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Text */}
          <Box sx={styles.textBox}>
            {botMessagesLoading ? (
              <>
                <Skeleton
                  variant="text"
                  width="42%"
                  sx={{ ...styles.name, ...styles.skeletonTitle }}
                />
                <Skeleton
                  variant="rounded"
                  width="88%"
                  sx={{ ...styles.message, ...styles.skeletonLine }}
                />
                <Skeleton
                  variant="rounded"
                  width="72%"
                  sx={{ ...styles.message, ...styles.skeletonLine }}
                />
              </>
            ) : (
              <>
                <Typography variant="subtitle1" sx={styles.name}>
                  {renderTypedMessage(headerTextForRender, headerVisibleChars)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={styles.message}
                  dangerouslySetInnerHTML={{ __html: resumeMessage }}
                />
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
